import CourseCard, { type Course } from '@/components/CourseCard';
import EmptyState from '@/components/common/EmptyState';
import useThemeColors from '@/hooks/useThemeColors';
import { User } from '@/types';
import { router } from '@inertiajs/react';
import { BookOpen } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface Category {
    id: number;
    name: string;
    courses: Course[];
}

interface Props {
    user: User;
    categories_with_courses?: Category[];
}

const Courses: React.FC<Props> = ({ user, categories_with_courses = [] }) => {
    const colors = useThemeColors();
    const { t } = useTranslation();

    const handleCourseClick = (course: Course) => {
        // Navigate to course detail page
        router.visit(`/courses/${course.slug}`);
    };

    // Calculate total courses count
    const totalCourses = categories_with_courses.reduce(
        (sum, category) => sum + category.courses.length,
        0
    );

    if (totalCourses === 0) {
        return (
            <div className="mx-auto max-w-6xl">
                <EmptyState
                    icon={BookOpen}
                    title={t('myAccount.noCoursesTitle')}
                    description={t('myAccount.noCoursesDescription')}
                />
            </div>
        );
    }

    return (
        <div className="mx-auto max-w-6xl">
            {/* Header with course count */}
            <div className="mb-6 flex items-center justify-between">
                <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">
                    {totalCourses}{' '}
                    {t('myAccount.coursesCount', { count: totalCourses })}
                </h2>
                <div className="flex gap-2">
                    <button
                        className={`rounded-lg border px-4 py-2 ${colors.border} ${colors.buttonSecondary}`}
                        type="button"
                        onClick={() =>
                            router.visit('/my-account/?tab=courses', {
                                preserveState: true,
                                preserveScroll: true,
                            })
                        }
                    >
                        {t('myAccount.myCourses')}
                    </button>
                    <button
                        className="rounded-lg border border-gray-300 px-4 py-2 text-gray-600 transition hover:bg-gray-50"
                        type="button"
                        onClick={() =>
                            router.visit('/my-account/?tab=certificates', {
                                preserveState: true,
                                preserveScroll: true,
                            })
                        }
                    >
                        {t('myAccount.myCertificates')}
                    </button>
                </div>
            </div>

            {/* Courses grouped by category */}
            {categories_with_courses.map((category) => (
                <div key={category.id} className="mb-8">
                    <h3 className="mb-4 text-xl font-semibold text-gray-900 dark:text-white">
                        {category.name}
                    </h3>
                    <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {category.courses.map((course) => (
                            <div key={course.id}>
                                <CourseCard
                                    course={course}
                                    onClick={() => handleCourseClick(course)}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default Courses;
